import { CSSProperties, useState } from 'react';
import { TableHeaderCell, InfoButton, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowUpRegular, ArrowDownRegular, ArrowSortRegular } from '@fluentui/react-icons';
import { FilterColumn } from './types';
import { ViewGroup, ViewSort } from './viewDefinition';
import { ColumnHeaderMenu } from './ColumnHeaderMenu';
import { ColumnHeaderFilter } from './ColumnHeaderFilter';
import { handleActivateKeyDown, toAriaSort } from '../../utils/a11y';
import i18n from '../../i18n';

// Unified list-filters program (spec 2026-07-22) — one table header cell combining URL-driven sort
// (click to cycle asc → desc → off) with the Dataverse-style per-column filter funnel. Replaces the
// page-local SortableHeader + toolbar-dropdown split.

const useStyles = makeStyles({
  cell: {
    userSelect: 'none',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  sortTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  sortIcon: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  sortIconActive: {
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
  },
});

export interface ListColumnHeaderProps<T> {
  column: FilterColumn<T>;
  sort: ViewSort | null;
  onSortChange: (next: ViewSort | null) => void;
  filters: Record<string, string>;
  onFiltersChange: (next: Record<string, string>) => void;
  style?: CSSProperties;
  // ── Contract v2 §13: the column header menu. All optional — a table that omits them simply does
  // not offer those items (C58, C66). ──
  group?: ViewGroup | null;
  onGroupChange?: (next: ViewGroup | null) => void;
  /** The user's width for this column (per-user preference, never the URL — M2). */
  width?: number;
  /** The column's default width in px, the base for Wider / Narrower. */
  defaultWidth?: number;
  onWidthChange?: (px: number | null) => void;
  canMoveLeft?: boolean;
  canMoveRight?: boolean;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  /** C86 facets for a lookup column: pass `() => list.facetCountsFor(column.key)` from `useListView`. */
  getFacetCounts?: () => Record<string, number>;
  /** A lookup column's options when the column cannot declare them: `() => list.lookupOptionsFor(column.key)`. */
  getLookupOptions?: () => { value: string; label: string }[];
}

export function ListColumnHeader<T>({
  column, sort, onSortChange, filters, onFiltersChange, style,
  group, onGroupChange, width, defaultWidth = 120, onWidthChange,
  canMoveLeft, canMoveRight, onMoveLeft, onMoveRight, getFacetCounts, getLookupOptions,
}: ListColumnHeaderProps<T>) {
  const styles = useStyles();
  // "Filter by…" in the menu opens this column's own staging popover.
  const [filterRequest, setFilterRequest] = useState(0);
  const sortable = !!column.sortKey;
  const direction = sortable && sort && sort.col === column.sortKey ? sort.dir : null;

  const cycleSort = () => {
    if (!column.sortKey) return;
    if (direction === null) onSortChange({ col: column.sortKey, dir: 'asc' });
    else if (direction === 'asc') onSortChange({ col: column.sortKey, dir: 'desc' });
    else onSortChange(null);
  };

  const sortIcon = () => {
    if (direction === 'asc') return <ArrowUpRegular className={styles.sortIconActive} />;
    if (direction === 'desc') return <ArrowDownRegular className={styles.sortIconActive} />;
    return <ArrowSortRegular className={styles.sortIcon} />;
  };

  return (
    <TableHeaderCell
      className={styles.cell}
      aria-sort={sortable ? toAriaSort(direction) : undefined}
      style={width !== undefined ? { ...style, width: `${width}px` } : style}
    >
      <div className={styles.content}>
        {sortable ? (
          <span
            className={styles.sortTrigger}
            role="button"
            tabIndex={0}
            onClick={cycleSort}
            onKeyDown={handleActivateKeyDown(cycleSort)}
          >
            {column.label}
            {sortIcon()}
          </span>
        ) : (
          <span>{column.label}</span>
        )}
        {column.info ? (
          // Sibling of the sort trigger (never nested inside it) so activating the info tip
          // doesn't also toggle the sort. InfoButton is the Fluent info-glyph primitive.
          <InfoButton
            size="small"
            info={column.info}
            aria-label={i18n.t('filters.info', { column: column.label })}
          />
        ) : null}
        {column.filterType ? (
          <ColumnHeaderFilter column={column} value={filters} onChange={onFiltersChange} openRequest={filterRequest} getFacetCounts={getFacetCounts} getLookupOptions={getLookupOptions} />
        ) : null}
        <ColumnHeaderMenu
          column={column}
          onSortChange={onSortChange}
          group={group}
          onGroupChange={onGroupChange}
          onFilterRequest={column.filterType ? () => setFilterRequest((n) => n + 1) : undefined}
          width={width}
          defaultWidth={defaultWidth}
          onWidthChange={onWidthChange}
          canMoveLeft={canMoveLeft}
          canMoveRight={canMoveRight}
          onMoveLeft={onMoveLeft}
          onMoveRight={onMoveRight}
        />
      </div>
    </TableHeaderCell>
  );
}
